/**
 * @fileoverview Global quick-capture — the TODO Funnel's zero-friction intake.
 * @module components/QuickCapture/QuickCapture
 *
 * Capture is sacred: press `q` anywhere (or Cmd/Ctrl+Shift+K, or tap the
 * floating button on mobile), type a thought, Enter. It lands in the GTD
 * inbox via POST /api/v1/capture; triage happens later. First line becomes
 * the title, further lines the detail (Shift+Enter for a new line).
 *
 * docs/TODO-FUNNEL-VISION.md Phase 1.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Paperclip } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './QuickCapture.css';

const isTypingTarget = (el) =>
  el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

// Mirrors the server's paste-mode split (routes/capture.js LIST_LINE_RE):
// ≥2 list-marker lines → each becomes its own task.
const LIST_LINE_RE = /^\s*(?:[-*•]|\d+[.)]|\[[ xX]\])\s+\S/;
const countListItems = (text) =>
  text.split('\n').filter((line) => LIST_LINE_RE.test(line)).length;

// ── File import (Wave 1, TASK-CONNECTIONS-AND-AGENTIC-FLOW §2 Tier 0) ──
// Files never upload anywhere: they parse in the browser into a bullet
// list that FILLS THE TEXTAREA, so the user reviews/edits before the
// normal paste-mode capture runs. Spreadsheets pick the most task-ish
// column by header; text files pass through as-is.
const TITLE_HEADER_RE = /task|title|action|item|todo|to.?do|next/i;

async function fileToBullets(file) {
  const name = file.name.toLowerCase();
  if (/\.(md|txt)$/.test(name)) {
    return await file.text();
  }
  if (/\.(xlsx|xls|csv|tsv)$/.test(name)) {
    const XLSX = await import('xlsx'); // lazy — keeps the sheet lib out of the main bundle
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
    if (!rows.length) throw new Error('Empty sheet');
    const header = rows[0].map((c) => String(c ?? ''));
    let col = header.findIndex((h) => TITLE_HEADER_RE.test(h));
    const hasHeader = col !== -1;
    if (col === -1) col = 0;
    return rows
      .slice(hasHeader ? 1 : 0)
      .map((r) => String(r[col] ?? '').trim())
      .filter(Boolean)
      .map((t) => `- ${t}`)
      .join('\n');
  }
  throw new Error('Use .xlsx, .csv, .md, or .txt (Docs/Word/PDF arrive with the Wave 2 connectors)');
}

function QuickCapture() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [captured, setCaptured] = useState(0); // 0 = idle, n = tasks created
  const [importError, setImportError] = useState(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const queryClient = useQueryClient();

  const importFile = useCallback(async (file) => {
    if (!file) return;
    try {
      const bullets = await fileToBullets(file);
      if (!bullets.trim()) throw new Error('Nothing to import from that file');
      setImportError(null);
      setText((prev) => (prev.trim() ? `${prev.trimEnd()}\n${bullets}` : bullets));
      inputRef.current?.focus();
    } catch (e) {
      setImportError(e.message);
    }
  }, []);

  const listCount = countListItems(text);
  const pasteMode = listCount >= 2;

  const captureMutation = useMutation({
    mutationFn: (body) => apiClient.post('/api/v1/capture', body),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      // Instant enrichment lands async a few seconds after capture —
      // one delayed refetch picks up the AI-sharpened row.
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['tasks'] }), 5000);
      setText('');
      setCaptured(r?.count ?? (r?.tasks?.length || 1));
      setTimeout(() => {
        setCaptured(0);
        setOpen(false);
      }, 650);
    },
  });

  const submit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || captureMutation.isPending) return;
    captureMutation.mutate(
      pasteMode ? { mode: 'paste', text: trimmed } : { text: trimmed }
    );
  }, [text, pasteMode, captureMutation]);

  // Global hotkeys: `q` when not typing, Cmd/Ctrl+Shift+K always.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (open) return;
      const chord = (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k';
      const plainQ = e.key === 'q' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target);
      if (chord || plainQ) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const onInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setText('');
      setImportError(null);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <>
      {/* No floating button (wave 3, Ryan: chat is the ONE FAB). Capture
          entry points: `q`, Cmd/Ctrl+Shift+K, and the funnel's own
          + buttons on /tasks — which also cover touch devices. */}
      {open && (
        <div
          className="qc-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
              setText('');
              setImportError(null);
            }
          }}
        >
          <div
            className={`qc-modal ${captured ? 'qc-modal-captured' : ''}`}
            role="dialog"
            aria-label="Quick capture"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              importFile(e.dataTransfer.files?.[0]);
            }}
          >
            {captured > 0 ? (
              <div className="qc-done">
                <Check size={18} />
                <span>{captured > 1 ? `${captured} in the inbox` : 'In the inbox'}</span>
              </div>
            ) : (
              <>
                <textarea
                  ref={inputRef}
                  className="qc-input"
                  rows={2}
                  placeholder="Capture a thought… first line is the title"
                  value={text}
                  onChange={(e) => { setText(e.target.value); setImportError(null); }}
                  onKeyDown={onInputKeyDown}
                  disabled={captureMutation.isPending}
                />
                <div className="qc-footer">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls,.csv,.tsv,.md,.txt"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      importFile(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="qc-attach"
                    title="Import a file (.xlsx, .csv, .md, .txt) — or drop it here"
                    onClick={() => fileRef.current?.click()}
                  >
                    <Paperclip size={14} />
                  </button>
                  {captureMutation.isError ? (
                    <span className="qc-error">Capture failed — try again</span>
                  ) : importError ? (
                    <span className="qc-error">{importError}</span>
                  ) : pasteMode ? (
                    <span className="qc-hint qc-hint-paste">
                      {listCount} items detected — each becomes a task
                    </span>
                  ) : (
                    <span className="qc-hint">Enter to capture · Shift+Enter for detail · Esc to close</span>
                  )}
                  <button
                    type="button"
                    className="qc-submit"
                    onClick={submit}
                    disabled={!text.trim() || captureMutation.isPending}
                  >
                    {pasteMode ? `Capture ${listCount}` : 'Capture'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default QuickCapture;
